import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Gift, Plus, Pencil, Trash2, Eye, EyeOff, Copy, Calendar, TrendingUp, Users, Tag, X, Upload, Check, Download } from 'lucide-react';
import { toast } from 'sonner';
import Footer from './Footer';

const API = process.env.REACT_APP_BACKEND_URL;

// Emoji picker data
const EMOJI_LIST = ['🎓', '💻', '📚', '🍕', '✈️', '🎬', '🏋️', '🎮', '🎨', '🎵', '📱', '🛍️', '🍔', '☕', '🚗', '🏠', '💼', '🎯', '🔥', '⭐', '🎁', '💰', '🎉', '🌟', '✨', '🚀', '💎', '🏆', '📈', '🎪'];

const AdminOffers = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('all');
  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [editingOffer, setEditingOffer] = useState(null);
  const [offerForm, setOfferForm] = useState({
    title: '',
    brand: '',
    category: '',
    discount_label: '',
    coupon_code: '',
    expiry_date: '',
    image_base64: '',
    instructions: ''
  });
  
  const [categoryForm, setCategoryForm] = useState({ name: '', emoji: '🎁' });
  const [deletingCategory, setDeletingCategory] = useState(null);
  
  const fileInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    // Check admin access
    if (!user || user.email !== 'shifracs@gmail.com') {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  useEffect(() => {
    if (user && user.email === 'shifracs@gmail.com') {
      fetchData();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (activeTab === 'all' || activeTab === 'create') {
        // Fetch offers
        const offersRes = await fetch(`${API}/api/offers/admin/all`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const offersData = await offersRes.json();
        if (offersData.success) {
          setOffers(offersData.offers);
        }
      }
      
      if (activeTab === 'categories' || activeTab === 'create') {
        // Fetch categories
        const catRes = await fetch(`${API}/api/offers/categories`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const catData = await catRes.json();
        if (catData.success) {
          setCategories(catData.categories);
        }
      }
      
      // Fetch stats
      const statsRes = await fetch(`${API}/api/offers/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statsData = await statsRes.json();
      if (statsData.success) {
        setStats(statsData.stats);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploadingImage(true);

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Accept any dimensions but optimize if too large
        let width = img.width;
        let height = img.height;
        
        // If image is extremely large, scale down proportionally (max 2000px on longest side)
        const maxDimension = 2000;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw image to canvas maintaining aspect ratio
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 with 82% quality
        const base64String = canvas.toDataURL('image/jpeg', 0.82);
        
        setOfferForm({ ...offerForm, image_base64: base64String });
        setUploadingImage(false);
        toast.success(`Image uploaded (${Math.round(width)}×${Math.round(height)}px)`);
      };
      img.onerror = () => {
        setUploadingImage(false);
        toast.error('Failed to load image');
      };
      img.src = event.target.result;
    };
    reader.onerror = () => {
      setUploadingImage(false);
      toast.error('Failed to read file');
    };
    reader.readAsDataURL(file);
  };

  const handleCreateOffer = async () => {
    if (!offerForm.title || !offerForm.brand || !offerForm.category || !offerForm.coupon_code || !offerForm.expiry_date || !offerForm.image_base64) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/offers/admin/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(offerForm)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Offer created successfully');
        resetOfferForm();
        setActiveTab('all');
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to create offer');
      }
    } catch (error) {
      console.error('Create error:', error);
      toast.error('Failed to create offer');
    }
  };

  const handleUpdateOffer = async () => {
    if (!editingOffer) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/offers/admin/update/${editingOffer.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(offerForm)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Offer updated successfully');
        setEditingOffer(null);
        resetOfferForm();
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to update offer');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update offer');
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/offers/admin/delete/${offerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Offer deleted successfully');
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to delete offer');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete offer');
    }
  };

  const handleTogglePublish = async (offer) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/offers/admin/update/${offer.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_published: !offer.is_published })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(offer.is_published ? 'Offer unpublished' : 'Offer published');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to toggle publish status');
    }
  };

  const handleEditOffer = (offer) => {
    setEditingOffer(offer);
    setOfferForm({
      title: offer.title,
      brand: offer.brand,
      category: offer.category,
      discount_label: offer.discount_label,
      coupon_code: offer.coupon_code,
      expiry_date: offer.expiry_date,
      image_base64: offer.image_base64,
      instructions: offer.instructions
    });
    setActiveTab('create');
  };

  const resetOfferForm = () => {
    setOfferForm({
      title: '',
      brand: '',
      category: '',
      discount_label: '',
      coupon_code: '',
      expiry_date: '',
      image_base64: '',
      instructions: ''
    });
    setEditingOffer(null);
  };


  const handleDownloadAnalytics = async (offerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/offers/admin/analytics/export/${offerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download analytics');
      }
      
      // Get filename from response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : 'offer_analytics.xlsx';
      
      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Analytics downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download analytics');
    }
  };

  const handleCreateCategory = async () => {
    if (!categoryForm.name) {
      toast.error('Please enter category name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Generate a UUID for the category
      const categoryId = `cat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const response = await fetch(`${API}/api/offers/admin/categories/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: categoryId,
          name: categoryForm.name,
          emoji: categoryForm.emoji
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Category created successfully');
        setCategoryForm({ name: '', emoji: '🎁' });
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to create category');
      }
    } catch (error) {
      console.error('Category creation error:', error);
      toast.error('Failed to create category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/offers/admin/categories/delete/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to delete category');
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        toast.success('Category deleted successfully');
        setDeletingCategory(null);
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Delete category error:', error);
      toast.error('Failed to delete category');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white pb-20" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900/40 to-purple-900/40 border-b border-gray-800 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Student Offers Admin
          </h1>
          <p className="text-gray-400 mt-1">Manage student discounts and offers</p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-gray-900/80 rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-5 h-5 text-indigo-400" />
              <p className="text-gray-400 text-sm">Total Offers</p>
            </div>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {stats.total_offers || 0}
            </p>
          </div>
          
          <div className="bg-gray-900/80 rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-5 h-5 text-green-400" />
              <p className="text-gray-400 text-sm">Published</p>
            </div>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {stats.published_offers || 0}
            </p>
          </div>
          
          <div className="bg-gray-900/80 rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-2 mb-2">
              <Copy className="w-5 h-5 text-purple-400" />
              <p className="text-gray-400 text-sm">Total Copies</p>
            </div>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {stats.total_copies || 0}
            </p>
          </div>
          
          <div className="bg-gray-900/80 rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-blue-400" />
              <p className="text-gray-400 text-sm">Total Views</p>
            </div>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {stats.total_views || 0}
            </p>
          </div>
          
          <div className="bg-gray-900/80 rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-2 mb-2">
              <Tag className="w-5 h-5 text-yellow-400" />
              <p className="text-gray-400 text-sm">Categories</p>
            </div>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {stats.total_categories || 0}
            </p>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="max-w-7xl mx-auto px-4 mb-6">
        <div className="flex gap-2 bg-gray-900/50 p-1 rounded-lg">
          <button
            onClick={() => { setActiveTab('all'); resetOfferForm(); }}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
              activeTab === 'all'
                ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            All Offers
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
              activeTab === 'create'
                ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {editingOffer ? 'Edit Offer' : 'Create Offer'}
          </button>
          <button
            onClick={() => { setActiveTab('categories'); resetOfferForm(); }}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
              activeTab === 'categories'
                ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Categories
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4">
        {/* All Offers Tab */}
        {activeTab === 'all' && (
          <div className="space-y-4">
            {loading ? (
              [1, 2, 3].map(i => <div key={i} className="bg-gray-900/50 h-40 rounded-xl animate-pulse" />)
            ) : offers.length === 0 ? (
              <div className="text-center py-20">
                <Gift className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No offers created yet</p>
              </div>
            ) : (
              offers.map(offer => (
                <div key={offer.id} className="bg-gray-900/80 rounded-xl p-4 border border-gray-800 hover:border-indigo-500/50 transition-colors">
                  <div className="flex gap-4">
                    {/* Thumbnail */}
                    <div className="flex-shrink-0">
                      <img
                        src={offer.image_base64}
                        alt={offer.title}
                        className="w-24 h-24 object-cover rounded-lg" loading="lazy" decoding="async"/>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-bold text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            {offer.title}
                          </h3>
                          <p className="text-gray-400 text-sm">{offer.brand}</p>
                        </div>
                        <div className="flex gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            offer.is_published
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-gray-700 text-gray-400'
                          }`}>
                            {offer.is_published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
                        <span className="bg-gray-800 px-2 py-1 rounded">
                          {categories.find(c => c.id === offer.category)?.emoji} {categories.find(c => c.id === offer.category)?.name}
                        </span>
                        <span className="font-mono font-bold text-white">{offer.coupon_code}</span>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(offer.expiry_date).toLocaleDateString()}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-1 text-gray-400">
                          <Copy className="w-4 h-4" />
                          <span>{offer.copies_count || 0} Copied Offer Code</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-400">
                          <Eye className="w-4 h-4" />
                          <span>{offer.views_count || 0} URL Visits</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="mt-4 pt-4 border-t border-gray-800 flex gap-2">
                    <button
                      onClick={() => handleEditOffer(offer)}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 text-indigo-400 rounded-lg hover:bg-indigo-500/30 transition-colors"
                    >
                      <Pencil className="w-4 h-4" /> Edit
                    </button>
                    <button
                      onClick={() => handleTogglePublish(offer)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                        offer.is_published
                          ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                          : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                      }`}
                    >
                      {offer.is_published ? <><EyeOff className="w-4 h-4" /> Unpublish</> : <><Eye className="w-4 h-4" /> Publish</>}
                    </button>
                    <button
                      onClick={() => handleDeleteOffer(offer.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                    <button
                      onClick={() => handleDownloadAnalytics(offer.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors ml-auto"
                      title="Download user analytics"
                    >
                      <Download className="w-4 h-4" /> Download Analytics
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Create/Edit Offer Tab */}
        {activeTab === 'create' && (
          <div className="bg-gray-900/80 rounded-xl p-6 border border-gray-800">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {editingOffer ? 'Edit Offer' : 'Create New Offer'}
            </h2>
            
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Offer Title *</label>
                <input
                  type="text"
                  value={offerForm.title}
                  onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                  placeholder="e.g., 50% Off on All Courses"
                  style={{ color: 'white' }}
                />
              </div>
              
              {/* Brand */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Brand Name *</label>
                <input
                  type="text"
                  value={offerForm.brand}
                  onChange={(e) => setOfferForm({ ...offerForm, brand: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                  placeholder="e.g., Udemy, Swiggy, Amazon"
                  style={{ color: 'white' }}
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category *</label>
                  <select
                    value={offerForm.category}
                    onChange={(e) => setOfferForm({ ...offerForm, category: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500"
                    style={{ color: 'white' }}
                  >
                    <option value="" style={{ color: 'gray' }}>Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id} style={{ color: 'white' }}>{cat.emoji} {cat.name}</option>
                    ))}
                  </select>
                </div>
                
                {/* Discount Label */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Discount Label *</label>
                  <input
                    type="text"
                    value={offerForm.discount_label}
                    onChange={(e) => setOfferForm({ ...offerForm, discount_label: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                    placeholder="e.g., 50% OFF, Buy 1 Get 1"
                    style={{ color: 'white' }}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Coupon Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Coupon Code *</label>
                  <input
                    type="text"
                    value={offerForm.coupon_code}
                    onChange={(e) => setOfferForm({ ...offerForm, coupon_code: e.target.value.toUpperCase() })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 font-mono focus:outline-none focus:border-indigo-500"
                    placeholder="e.g., STUDENT50"
                    style={{ color: 'white' }}
                  />
                </div>
                
                {/* Expiry Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Expiry Date *</label>
                  <input
                    type="date"
                    value={offerForm.expiry_date}
                    onChange={(e) => setOfferForm({ ...offerForm, expiry_date: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500"
                    style={{ color: 'white', colorScheme: 'dark' }}
                  />
                </div>
              </div>
              
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Offer Image * 
                  <span className="text-xs text-gray-500 ml-2">(Recommended: 800×500px for best display)</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                {offerForm.image_base64 ? (
                  <div className="relative">
                    <img src={offerForm.image_base64} alt="Preview" className="w-full h-48 object-cover rounded-lg" loading="lazy" decoding="async"/>
                    <button
                      onClick={() => setOfferForm({ ...offerForm, image_base64: '' })}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 rounded-full p-2 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="w-full border-2 border-dashed border-gray-700 rounded-lg py-12 hover:border-indigo-500 transition-colors flex flex-col items-center gap-2"
                  >
                    <Upload className="w-8 h-8 text-gray-500" />
                    <p className="text-gray-400">{uploadingImage ? 'Uploading...' : 'Click to upload or drag & drop'}</p>
                    <p className="text-xs text-gray-500">PNG, JPG • Any dimension accepted • Max 10MB</p>
                    <p className="text-xs text-indigo-400 mt-1">💡 Tip: 800×500px works best for consistent display</p>
                  </button>
                )}
              </div>
              
              {/* Instructions */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">How to Avail Instructions</label>
                <textarea
                  value={offerForm.instructions}
                  onChange={(e) => setOfferForm({ ...offerForm, instructions: e.target.value })}
                  rows={6}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500"
                  placeholder="Enter step-by-step instructions (one per line).\nURLs will be automatically clickable."
                />
              </div>
              
              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={editingOffer ? handleUpdateOffer : handleCreateOffer}
                  className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 rounded-lg font-bold hover:shadow-lg transition-all"
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  {editingOffer ? 'Update Offer' : 'Create Offer'}
                </button>
                {editingOffer && (
                  <button
                    onClick={() => { resetOfferForm(); setActiveTab('all'); }}
                    className="px-6 bg-gray-700 text-white py-3 rounded-lg font-medium hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="space-y-6">
            {/* Create Category */}
            <div className="bg-gray-900/80 rounded-xl p-6 border border-gray-800">
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Create New Category
              </h2>
              
              <div className="space-y-4">
                {/* Category Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category Name *</label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                    placeholder="e.g., Education, Food, Travel"
                    style={{ color: 'white' }}
                  />
                </div>
                
                {/* Emoji Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Select Icon *</label>
                  <select
                    value={categoryForm.emoji}
                    onChange={(e) => setCategoryForm({ ...categoryForm, emoji: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500 text-xl"
                    style={{ color: 'white' }}
                  >
                    <option value="🎓">🎓 Education</option>
                    <option value="💻">💻 Software & Tech</option>
                    <option value="📚">📚 Books & Learning</option>
                    <option value="🍕">🍕 Food & Dining</option>
                    <option value="🍔">🍔 Fast Food</option>
                    <option value="☕">☕ Coffee & Café</option>
                    <option value="✈️">✈️ Travel & Tourism</option>
                    <option value="🚗">🚗 Transportation</option>
                    <option value="🏠">🏠 Home & Living</option>
                    <option value="🎬">🎬 Entertainment</option>
                    <option value="🎮">🎮 Gaming</option>
                    <option value="🎵">🎵 Music & Audio</option>
                    <option value="🎨">🎨 Arts & Crafts</option>
                    <option value="🏋️">🏋️ Fitness & Sports</option>
                    <option value="💼">💼 Business & Office</option>
                    <option value="📱">📱 Mobile & Gadgets</option>
                    <option value="🛍️">🛍️ Shopping & Retail</option>
                    <option value="🎯">🎯 Services</option>
                    <option value="🔥">🔥 Hot Deals</option>
                    <option value="⭐">⭐ Featured</option>
                    <option value="🎁">🎁 Gifts & Occasions</option>
                    <option value="💰">💰 Finance & Banking</option>
                    <option value="🎉">🎉 Events & Party</option>
                    <option value="🌟">🌟 Premium</option>
                    <option value="✨">✨ Special Offers</option>
                    <option value="🚀">🚀 Trending</option>
                    <option value="💎">💎 Exclusive</option>
                    <option value="🏆">🏆 Top Rated</option>
                    <option value="📈">📈 Best Value</option>
                    <option value="🎪">🎪 Fun & Recreation</option>
                  </select>
                </div>
                
                {/* Submit Button */}
                <button
                  onClick={handleCreateCategory}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  <Plus className="w-5 h-5" /> Create Category
                </button>
              </div>
            </div>
            
            {/* Categories List */}
            <div className="bg-gray-900/80 rounded-xl p-6 border border-gray-800">
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                All Categories
              </h2>
              
              {categories.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No categories created yet</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {categories.map(cat => (
                    <div
                      key={cat.id}
                      className="bg-gray-800 rounded-lg p-4 flex items-center justify-between hover:bg-gray-750 transition-colors border border-gray-700"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{cat.emoji}</span>
                        <span className="font-medium text-white">{cat.name}</span>
                      </div>
                      <button
                        onClick={() => setDeletingCategory(cat)}
                        className="bg-red-500/20 hover:bg-red-500/30 text-red-400 hover:text-red-300 p-2 rounded-lg transition-all"
                        title="Delete category"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingCategory && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 max-w-md w-full">
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-red-500/20 p-3 rounded-full">
                <Trash2 className="w-6 h-6 text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Delete Category
                </h3>
                <p className="text-gray-400">
                  Are you sure you want to delete <span className="text-white font-semibold">{deletingCategory.emoji} {deletingCategory.name}</span>?
                </p>
                <p className="text-red-400 text-sm mt-2">
                  This action cannot be undone. Make sure no offers are using this category.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeletingCategory(null)}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCategory(deletingCategory.id)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminOffers;